<template>
  <!-- Form -->
  <form
    :class="[
      'form',
      { 'form--light': light },
    ]"
  >
    <!-- Form group -->
    <div
      v-for="(field, index) in data"
      :key="index"
      class="form-group"
    >
      <!-- Separator -->
      <hr v-if="field.type === 'separator'">
      <!-- Title -->
      <h4 v-if="field.type === 'title'">
        {{ $t(field.label) }}
      </h4>
      <!-- Label -->
      <label
        v-if="!['separator', 'title', 'checkbox', 'select_region'].includes(field.type)
          && (!field.isManualEntry
            || (field.isManualEntry && manualEntry))"
        :for="field.model"
        class="form-label"
        v-html="formatInputLabel(field)"
      />
      <!-- Label select region -->
      <label
        v-if="['select_region'].includes(field.type)
          && inputFields[field.sibling_model]
          && (inputFields[field.sibling_model] === 'CA'
            || inputFields[field.sibling_model] === 'US')"
        :for="field.model"
        class="form-label"
        v-html="formatInputLabel(field)"
      />
      <!-- Input text -->
      <input
        v-if="['text', 'email'].includes(field.type)
          && (!field.isManualEntry || field.isManualEntry && manualEntry)"
        :id="field.model"
        v-model="inputFields[field.model]"
        v-validate="field.validations"
        :type="field.type"
        :data-vv-name="field.model"
        :name="field.model"
        :data-vv-scope="field.model"
        :placeholder="field.placeholder"
        :class="[
          'form-input',
          { 'is-invalid': fieldIsInvalid(field.model) || (field.type === 'email' && emailAlreadyUsed) }
        ]"
        :maxlength="field.maxLength"
        v-on="field.type === 'email' ? { blur: checkEmail } : {}"
      >
      <!-- Input password -->
      <input
        v-if="['password'].includes(field.name)"
        :id="field.model"
        v-model="inputFields[field.model]"
        v-validate="field.validations"
        :type="field.type"
        :data-vv-name="field.model"
        :name="field.model"
        :data-vv-scope="field.model"
        :class="{
          'form-input': true,
          'is-invalid': fieldIsInvalid(field.model) || (passwordFeedbackVisible && !passwordFormatValid),
          'is-success': passwordFeedbackVisible && passwordFormatValid,
        }"
        :maxlength="field.maxLength"
        @input="handlePasswordInput"
      >
      <!-- Input password feedback -->
      <div
        v-if="field.model === 'password' && passwordFeedbackVisible"
        :class="[
          'form-password-feedback',
          { 'is-success': passwordFormatValid },
        ]"
      >
        <div
          v-for="(condition, conditionIndex) in Object.entries(field.requirements)"
          :key="`pw-req-${conditionIndex}-${condition[conditionIndex]}`"
        >
          <template v-if="condition[1] === false">
            <i class="material-icons is-danger">close</i>
            <span class="is-danger">
              {{ formatFeedback(condition) }}
            </span>
          </template>
          <template v-else>
            <i class="material-icons is-success">check</i>
            <span class="is-success">
              {{ formatFeedback(condition) }}
            </span>
          </template>
        </div>
      </div>
      <!-- Input password confirmation -->
      <input
        v-if="['password_confirmation'].includes(field.name)"
        :id="field.model"
        v-model="inputFields[field.model]"
        v-validate="field.validations"
        :type="field.type"
        :data-vv-name="field.model"
        :name="field.model"
        :data-vv-scope="field.model"
        :class="{
          'form-input': true,
          'is-invalid': fieldIsInvalid(field.model),
        }"
        :maxlength="field.maxLength"
        @input="checkPasswordMatch"
      >
      <!-- Input date dropdown -->
      <date-dropdown
        v-if="['date'].includes(field.type)"
        :id="field.model"
        v-model="inputFields[field.model]"
        :i18n="$t('form.months')"
        :max-year="maxYear"
      />
      <!-- Label date disclaimer -->
      <span
        v-if="['date'].includes(field.type)"
        class="form-date-disclaimer"
        v-html="$t('form.date_disclaimer')"
      />

      <!-- Input select with vue-select -->
      <div
        v-if="['select'].includes(field.type)"
        v-click-outside="() => closeSelect(`select_${index}`)"
        class="select-wrapper"
      >
        <v-select
          :ref="`select_${index}`"
          :id="field.model"
          v-model="inputFields[field.model]"
          v-validate="field.validations"
          :options="field.options"
          :reduce="option => option.value"
          label="label"
          :data-vv-name="field.model"
          :placeholder="$t(`form.select_${field.name}`)"
          :searchable="false"
          :clearable="false"
          :class="{
            'is-invalid': fieldIsInvalid(field.model)
          }"
        />
      </div>

      <!-- Input Google autocomplete -->
      <template v-if="['autocomplete'].includes(field.type)">
        <vue-google-autocomplete
          :id="field.model"
          v-model="inputFields.autocompleteAddress"
          v-validate="{ required: !manualEntry }"
          :class="{
            'is-invalid': fieldIsInvalid(field.model)
          }"
          :data-vv-name="field.model"
          :placeholder="$t('form.start_typing')"
          :enable-geolocation="true"
          class="form-input"
          type="search"
          @placechanged="fillAddressData"
        />
        <a
          v-if="!manualEntry"
          href="#"
          class="form-manual-entry"
          @click.prevent="handleToggleAddress"
        >
          {{ $t('form.enter_address') }}
        </a>
      </template>
      <!-- Input checkbox -->
      <Checkbox
        v-if="['checkbox'].includes(field.type)"
        :id="field.model"
        v-model="inputFields[field.model]"
        v-validate="field.validations"
        :data-vv-name="field.model"
        :label="field.label"
        :value="inputFields[field.model]"
      />
      <!-- Input select country with vue-select -->
      <div
        v-if="['select_country'].includes(field.type)
          && (field.isManualEntry && manualEntry)"
        v-click-outside="() => closeSelect(`select_country_${index}`)"
        class="select-wrapper"
      >
        <v-select
          :ref="`select_country_${index}`"
          :id="field.model"
          v-model="inputFields[field.model]"
          v-validate="field.validations"
          :options="countryOptions"
          :reduce="option => option.value"
          label="label"
          :data-vv-name="field.model"
          :placeholder="$t('form.select_country')"
          :searchable="false"
          :clearable="false"
          :class="{
            'is-invalid': fieldIsInvalid(field.model)
          }"
        />
      </div>

      <!-- Input select region with vue-select (shows only when CA or US is selected) -->
      <div
        v-if="['select_region'].includes(field.type)
          && ((inputFields[field.sibling_model] && inputFields[field.sibling_model] === 'CA')
            || inputFields[field.sibling_model] === 'US')
          && (field.isManualEntry && manualEntry)"
        v-click-outside="() => closeSelect(`select_region_${index}`)"
        class="select-wrapper"
      >
        <v-select
          :ref="`select_region_${index}`"
          :id="field.model"
          v-model="inputFields[field.model]"
          v-validate="field.validations"
          :options="getRegionOptions(inputFields[field.sibling_model])"
          :reduce="option => option.value"
          label="label"
          :data-vv-name="field.model"
          :placeholder="$t('form.select_region')"
          :searchable="false"
          :clearable="false"
          :class="{
            'is-invalid': fieldIsInvalid(field.model)
          }"
        />
      </div>
      <!-- Input validation alert -->
      <Alert
        v-if="fieldIsInvalid(field.model)"
        :message="formatValidationMessage(field)"
        :type="['autocomplete'].includes(field.type) ? 'overflow' : null"
        :light="isCreate"
      />
    </div>
    <!-- Terms & Conditions -->
    <div
      v-if="isCreate"
      class="form-group"
    >
      <Checkbox
        v-if="isCreate"
        id="terms"
        v-model="termsChecked"
      >
        <p
          v-if="isCreate"
          class="form-terms"
        >
          {{ $t('form.terms_desc1') }}
          <a
            :href="$t('form.terms_url')"
            target="_blank"
          >
            {{ $t('form.terms_desc2') }}
          </a>
          <template v-if="isSiteGamification">
            {{ $t('form.terms_desc3') }}
            <a
              :href="$t('chgamification.rules')"
              target="_blank"
            >
              {{ $t('form.terms_desc4') }}
            </a>
          </template>
        </p>
      </Checkbox>
    </div>

    <div
      v-if="isCreate"
      class="form-group"
    >
      <p
        v-if="isCreate"
        class="optin"
      >
        {{ $t('form.optin_desc1') }}
        <a
          :href="$t('form.optin_privacy_policies_url')"
          target="_blank"
        >
          {{ $t('form.optin_desc2') }}
        </a>
        {{ $t('form.optin_desc3') }}
        <a
          :href="$t('form.optin_terms_conditions_url')"
          target="_blank"
        >
          {{ $t('form.optin_desc4') }}
        </a>
      </p>
    </div>
    <!-- Submit -->
    <div
      :class="[
        'form-submit',
        { 'form-submit--multiple-links': links && links.length > 1 },
      ]"
    >
      <!-- Invisible Recaptcha -->
      <invisible-recaptcha
        :sitekey="recaptchaSiteKey"
        :callback="handleSubmit"
        :class="{
          'is-loading': isLoading,
        }"
        class="btn"
        type="submit"
      >
        <template v-if="isLoading">
          <span class="loading-spin-container">
            <i class="material-icons loading-spin">replay</i>
          </span>
        </template>
        <span class="btn__label">{{ btnLabel }}</span>
      </invisible-recaptcha>
      <!-- Links -->
      <template v-if="links">
        <router-link
          v-for="link in links"
          :key="link.label"
          :to="link.to"
          class="link-alt"
        >
          {{ link.label }}
        </router-link>
      </template>
    </div>
    <!-- General alert -->
    <Alert
      v-if="formMessage"
      :message="formMessage"
      :light="isCreate"
      type="single"
    />
  </form>
</template>

<script>
import { mapState } from 'vuex';
import Cookies from 'js-cookie';
import { get } from 'lodash';

import settings from '@/mixins/settings';
import redirect from '@/mixins/redirect';

import VueGoogleAutocomplete from 'vue-google-autocomplete';
import InvisibleRecaptcha from 'vue-invisible-recaptcha';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import Alert from '@/components/Alert.vue';
import Checkbox from '@/components/Checkbox.vue';
import DateDropdown from '@/components/DateDropdown.vue';

export default {
  name: 'Form',
  directives: {
    clickOutside: {
      bind (el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('mousedown', el._clickOutsideHandler)
      },
      unbind (el) {
        document.removeEventListener('mousedown', el._clickOutsideHandler)
      }
    }
  },
  components: {
    VueGoogleAutocomplete,
    InvisibleRecaptcha,
    Alert,
    Checkbox,
    DateDropdown,
    vSelect,
  },
  mixins: [settings, redirect],
  props: {
    data: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    links: {
      type: [Object, Array],
      required: false,
      default: null,
    },
    light: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data () {
    return {
      manualEntry: false,
      passwordFeedbackVisible: false,
      passwordFormatValid: false,
      bothPasswordMatch: null,
      emailAlreadyUsed: null,
      formMessage: null,
      termsChecked: false,
      isLoading: false,
      cachedEmail: '',
      inputFields: {
        firstName: this.$route.query.firstName || '',
        lastName: this.$route.query.lastName || '',
        gender: '',
        birthDate: '',
        email: this.$route.query.email || '',
        password: '',
        passwordConfirmation: '',
        phone: '',
        cellphone: '',
        autocompleteAddress: '',
        address: '',
        city: '',
        country: '',
        region: '',
        postalCode: '',
        chgmOptin: false,
        rememberMe: false,
      },
    };
  },
  computed: {
    ...mapState('formOptions', ['languages']),
    // Label of the submit button
    btnLabel () {
      return this.$t(`form.${this.isCreate ? 'become_member' : 'submit'}`);
    },
    // Check if the form should apply "create" conditions
    isCreate () {
      return this.type === 'create';
    },
    // Check if the form should apply "login" conditions
    isLogin () {
      return this.type === 'login';
    },
    // Check if the form should apply "forgot" conditions
    isForgot () {
      return this.type === 'forgot';
    },
    // Check if the form should apply "reset" conditions
    isReset () {
      return this.type === 'reset';
    },
    // Create a computed property for mutations
    countryInputField () {
      return this.inputFields.country;
    },
    // Use recaptcha site key
    recaptchaSiteKey () {
      return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        ? process.env.VUE_APP_RECAPTCHA_SITE_KEY
        : null;
    },
    // Formatted max year used in date dropdown (13 years old and older)
    maxYear () {
      return new Date().getFullYear() - 13;
    },
    // Check if the password fill all requirements
    passwordRequirements () {
      const entry = this.data.find(one => one.name === 'password');
      return entry.requirements;
    },
    /**
     * Get country options with Canada and US at the top
     * @returns {Array} - Array of country options
     */
    countryOptions () {
      const countries = [
        { code: 'CA', en: 'Canada', fr: 'Canada' },
        { code: 'US', en: 'United States', fr: 'États-Unis' },
        { code: '', en: '---', fr: '---' }, // Separator
        { code: 'AF', en: 'Afghanistan', fr: 'Afghanistan' },
        { code: 'AL', en: 'Albania', fr: 'Albanie' },
        { code: 'DZ', en: 'Algeria', fr: 'Algérie' },
        { code: 'AD', en: 'Andorra', fr: 'Andorre' },
        { code: 'AO', en: 'Angola', fr: 'Angola' },
        { code: 'AG', en: 'Antigua and Barbuda', fr: 'Antigua-et-Barbuda' },
        { code: 'AR', en: 'Argentina', fr: 'Argentine' },
        { code: 'AM', en: 'Armenia', fr: 'Arménie' },
        { code: 'AU', en: 'Australia', fr: 'Australie' },
        { code: 'AT', en: 'Austria', fr: 'Autriche' },
        { code: 'AZ', en: 'Azerbaijan', fr: 'Azerbaïdjan' },
        { code: 'BS', en: 'Bahamas', fr: 'Bahamas' },
        { code: 'BH', en: 'Bahrain', fr: 'Bahreïn' },
        { code: 'BD', en: 'Bangladesh', fr: 'Bangladesh' },
        { code: 'BB', en: 'Barbados', fr: 'Barbade' },
        { code: 'BY', en: 'Belarus', fr: 'Biélorussie' },
        { code: 'BE', en: 'Belgium', fr: 'Belgique' },
        { code: 'BZ', en: 'Belize', fr: 'Belize' },
        { code: 'BJ', en: 'Benin', fr: 'Bénin' },
        { code: 'BT', en: 'Bhutan', fr: 'Bhoutan' },
        { code: 'BO', en: 'Bolivia', fr: 'Bolivie' },
        { code: 'BA', en: 'Bosnia and Herzegovina', fr: 'Bosnie-Herzégovine' },
        { code: 'BW', en: 'Botswana', fr: 'Botswana' },
        { code: 'BR', en: 'Brazil', fr: 'Brésil' },
        { code: 'BN', en: 'Brunei', fr: 'Brunei' },
        { code: 'BG', en: 'Bulgaria', fr: 'Bulgarie' },
        { code: 'BF', en: 'Burkina Faso', fr: 'Burkina Faso' },
        { code: 'BI', en: 'Burundi', fr: 'Burundi' },
        { code: 'KH', en: 'Cambodia', fr: 'Cambodge' },
        { code: 'CM', en: 'Cameroon', fr: 'Cameroun' },
        { code: 'CV', en: 'Cape Verde', fr: 'Cap-Vert' },
        { code: 'CF', en: 'Central African Republic', fr: 'République centrafricaine' },
        { code: 'TD', en: 'Chad', fr: 'Tchad' },
        { code: 'CL', en: 'Chile', fr: 'Chili' },
        { code: 'CN', en: 'China', fr: 'Chine' },
        { code: 'CO', en: 'Colombia', fr: 'Colombie' },
        { code: 'KM', en: 'Comoros', fr: 'Comores' },
        { code: 'CG', en: 'Congo', fr: 'Congo' },
        { code: 'CR', en: 'Costa Rica', fr: 'Costa Rica' },
        { code: 'HR', en: 'Croatia', fr: 'Croatie' },
        { code: 'CU', en: 'Cuba', fr: 'Cuba' },
        { code: 'CY', en: 'Cyprus', fr: 'Chypre' },
        { code: 'CZ', en: 'Czech Republic', fr: 'République tchèque' },
        { code: 'DK', en: 'Denmark', fr: 'Danemark' },
        { code: 'DJ', en: 'Djibouti', fr: 'Djibouti' },
        { code: 'DM', en: 'Dominica', fr: 'Dominique' },
        { code: 'DO', en: 'Dominican Republic', fr: 'République dominicaine' },
        { code: 'TL', en: 'East Timor', fr: 'Timor oriental' },
        { code: 'EC', en: 'Ecuador', fr: 'Équateur' },
        { code: 'EG', en: 'Egypt', fr: 'Égypte' },
        { code: 'SV', en: 'El Salvador', fr: 'Salvador' },
        { code: 'GQ', en: 'Equatorial Guinea', fr: 'Guinée équatoriale' },
        { code: 'ER', en: 'Eritrea', fr: 'Érythrée' },
        { code: 'EE', en: 'Estonia', fr: 'Estonie' },
        { code: 'ET', en: 'Ethiopia', fr: 'Éthiopie' },
        { code: 'FJ', en: 'Fiji', fr: 'Fidji' },
        { code: 'FI', en: 'Finland', fr: 'Finlande' },
        { code: 'FR', en: 'France', fr: 'France' },
        { code: 'GA', en: 'Gabon', fr: 'Gabon' },
        { code: 'GM', en: 'Gambia', fr: 'Gambie' },
        { code: 'GE', en: 'Georgia', fr: 'Géorgie' },
        { code: 'DE', en: 'Germany', fr: 'Allemagne' },
        { code: 'GH', en: 'Ghana', fr: 'Ghana' },
        { code: 'GR', en: 'Greece', fr: 'Grèce' },
        { code: 'GD', en: 'Grenada', fr: 'Grenade' },
        { code: 'GT', en: 'Guatemala', fr: 'Guatemala' },
        { code: 'GN', en: 'Guinea', fr: 'Guinée' },
        { code: 'GW', en: 'Guinea-Bissau', fr: 'Guinée-Bissau' },
        { code: 'GY', en: 'Guyana', fr: 'Guyana' },
        { code: 'HT', en: 'Haiti', fr: 'Haïti' },
        { code: 'HN', en: 'Honduras', fr: 'Honduras' },
        { code: 'HU', en: 'Hungary', fr: 'Hongrie' },
        { code: 'IS', en: 'Iceland', fr: 'Islande' },
        { code: 'IN', en: 'India', fr: 'Inde' },
        { code: 'ID', en: 'Indonesia', fr: 'Indonésie' },
        { code: 'IR', en: 'Iran', fr: 'Iran' },
        { code: 'IQ', en: 'Iraq', fr: 'Irak' },
        { code: 'IE', en: 'Ireland', fr: 'Irlande' },
        { code: 'IL', en: 'Israel', fr: 'Israël' },
        { code: 'IT', en: 'Italy', fr: 'Italie' },
        { code: 'JM', en: 'Jamaica', fr: 'Jamaïque' },
        { code: 'JP', en: 'Japan', fr: 'Japon' },
        { code: 'JO', en: 'Jordan', fr: 'Jordanie' },
        { code: 'KZ', en: 'Kazakhstan', fr: 'Kazakhstan' },
        { code: 'KE', en: 'Kenya', fr: 'Kenya' },
        { code: 'KI', en: 'Kiribati', fr: 'Kiribati' },
        { code: 'KP', en: 'North Korea', fr: 'Corée du Nord' },
        { code: 'KR', en: 'South Korea', fr: 'Corée du Sud' },
        { code: 'KW', en: 'Kuwait', fr: 'Koweït' },
        { code: 'KG', en: 'Kyrgyzstan', fr: 'Kirghizistan' },
        { code: 'LA', en: 'Laos', fr: 'Laos' },
        { code: 'LV', en: 'Latvia', fr: 'Lettonie' },
        { code: 'LB', en: 'Lebanon', fr: 'Liban' },
        { code: 'LS', en: 'Lesotho', fr: 'Lesotho' },
        { code: 'LR', en: 'Liberia', fr: 'Libéria' },
        { code: 'LY', en: 'Libya', fr: 'Libye' },
        { code: 'LI', en: 'Liechtenstein', fr: 'Liechtenstein' },
        { code: 'LT', en: 'Lithuania', fr: 'Lituanie' },
        { code: 'LU', en: 'Luxembourg', fr: 'Luxembourg' },
        { code: 'MK', en: 'Macedonia', fr: 'Macédoine' },
        { code: 'MG', en: 'Madagascar', fr: 'Madagascar' },
        { code: 'MW', en: 'Malawi', fr: 'Malawi' },
        { code: 'MY', en: 'Malaysia', fr: 'Malaisie' },
        { code: 'MV', en: 'Maldives', fr: 'Maldives' },
        { code: 'ML', en: 'Mali', fr: 'Mali' },
        { code: 'MT', en: 'Malta', fr: 'Malte' },
        { code: 'MH', en: 'Marshall Islands', fr: 'Îles Marshall' },
        { code: 'MR', en: 'Mauritania', fr: 'Mauritanie' },
        { code: 'MU', en: 'Mauritius', fr: 'Maurice' },
        { code: 'MX', en: 'Mexico', fr: 'Mexique' },
        { code: 'FM', en: 'Micronesia', fr: 'Micronésie' },
        { code: 'MD', en: 'Moldova', fr: 'Moldavie' },
        { code: 'MC', en: 'Monaco', fr: 'Monaco' },
        { code: 'MN', en: 'Mongolia', fr: 'Mongolie' },
        { code: 'ME', en: 'Montenegro', fr: 'Monténégro' },
        { code: 'MA', en: 'Morocco', fr: 'Maroc' },
        { code: 'MZ', en: 'Mozambique', fr: 'Mozambique' },
        { code: 'MM', en: 'Myanmar', fr: 'Myanmar' },
        { code: 'NA', en: 'Namibia', fr: 'Namibie' },
        { code: 'NR', en: 'Nauru', fr: 'Nauru' },
        { code: 'NP', en: 'Nepal', fr: 'Népal' },
        { code: 'NL', en: 'Netherlands', fr: 'Pays-Bas' },
        { code: 'NZ', en: 'New Zealand', fr: 'Nouvelle-Zélande' },
        { code: 'NI', en: 'Nicaragua', fr: 'Nicaragua' },
        { code: 'NE', en: 'Niger', fr: 'Niger' },
        { code: 'NG', en: 'Nigeria', fr: 'Nigéria' },
        { code: 'NO', en: 'Norway', fr: 'Norvège' },
        { code: 'OM', en: 'Oman', fr: 'Oman' },
        { code: 'PK', en: 'Pakistan', fr: 'Pakistan' },
        { code: 'PW', en: 'Palau', fr: 'Palaos' },
        { code: 'PA', en: 'Panama', fr: 'Panama' },
        { code: 'PG', en: 'Papua New Guinea', fr: 'Papouasie-Nouvelle-Guinée' },
        { code: 'PY', en: 'Paraguay', fr: 'Paraguay' },
        { code: 'PE', en: 'Peru', fr: 'Pérou' },
        { code: 'PH', en: 'Philippines', fr: 'Philippines' },
        { code: 'PL', en: 'Poland', fr: 'Pologne' },
        { code: 'PT', en: 'Portugal', fr: 'Portugal' },
        { code: 'QA', en: 'Qatar', fr: 'Qatar' },
        { code: 'RO', en: 'Romania', fr: 'Roumanie' },
        { code: 'RU', en: 'Russia', fr: 'Russie' },
        { code: 'RW', en: 'Rwanda', fr: 'Rwanda' },
        { code: 'KN', en: 'Saint Kitts and Nevis', fr: 'Saint-Christophe-et-Niévès' },
        { code: 'LC', en: 'Saint Lucia', fr: 'Sainte-Lucie' },
        { code: 'VC', en: 'Saint Vincent and the Grenadines', fr: 'Saint-Vincent-et-les-Grenadines' },
        { code: 'WS', en: 'Samoa', fr: 'Samoa' },
        { code: 'SM', en: 'San Marino', fr: 'Saint-Marin' },
        { code: 'ST', en: 'Sao Tome and Principe', fr: 'Sao Tomé-et-Principe' },
        { code: 'SA', en: 'Saudi Arabia', fr: 'Arabie saoudite' },
        { code: 'SN', en: 'Senegal', fr: 'Sénégal' },
        { code: 'RS', en: 'Serbia', fr: 'Serbie' },
        { code: 'SC', en: 'Seychelles', fr: 'Seychelles' },
        { code: 'SL', en: 'Sierra Leone', fr: 'Sierra Leone' },
        { code: 'SG', en: 'Singapore', fr: 'Singapour' },
        { code: 'SK', en: 'Slovakia', fr: 'Slovaquie' },
        { code: 'SI', en: 'Slovenia', fr: 'Slovénie' },
        { code: 'SB', en: 'Solomon Islands', fr: 'Îles Salomon' },
        { code: 'SO', en: 'Somalia', fr: 'Somalie' },
        { code: 'ZA', en: 'South Africa', fr: 'Afrique du Sud' },
        { code: 'SS', en: 'South Sudan', fr: 'Soudan du Sud' },
        { code: 'ES', en: 'Spain', fr: 'Espagne' },
        { code: 'LK', en: 'Sri Lanka', fr: 'Sri Lanka' },
        { code: 'SD', en: 'Sudan', fr: 'Soudan' },
        { code: 'SR', en: 'Suriname', fr: 'Suriname' },
        { code: 'SZ', en: 'Swaziland', fr: 'Eswatini' },
        { code: 'SE', en: 'Sweden', fr: 'Suède' },
        { code: 'CH', en: 'Switzerland', fr: 'Suisse' },
        { code: 'SY', en: 'Syria', fr: 'Syrie' },
        { code: 'TW', en: 'Taiwan', fr: 'Taïwan' },
        { code: 'TJ', en: 'Tajikistan', fr: 'Tadjikistan' },
        { code: 'TZ', en: 'Tanzania', fr: 'Tanzanie' },
        { code: 'TH', en: 'Thailand', fr: 'Thaïlande' },
        { code: 'TG', en: 'Togo', fr: 'Togo' },
        { code: 'TO', en: 'Tonga', fr: 'Tonga' },
        { code: 'TT', en: 'Trinidad and Tobago', fr: 'Trinité-et-Tobago' },
        { code: 'TN', en: 'Tunisia', fr: 'Tunisie' },
        { code: 'TR', en: 'Turkey', fr: 'Turquie' },
        { code: 'TM', en: 'Turkmenistan', fr: 'Turkménistan' },
        { code: 'TV', en: 'Tuvalu', fr: 'Tuvalu' },
        { code: 'UG', en: 'Uganda', fr: 'Ouganda' },
        { code: 'UA', en: 'Ukraine', fr: 'Ukraine' },
        { code: 'AE', en: 'United Arab Emirates', fr: 'Émirats arabes unis' },
        { code: 'GB', en: 'United Kingdom', fr: 'Royaume-Uni' },
        { code: 'UY', en: 'Uruguay', fr: 'Uruguay' },
        { code: 'UZ', en: 'Uzbekistan', fr: 'Ouzbékistan' },
        { code: 'VU', en: 'Vanuatu', fr: 'Vanuatu' },
        { code: 'VA', en: 'Vatican City', fr: 'Cité du Vatican' },
        { code: 'VE', en: 'Venezuela', fr: 'Venezuela' },
        { code: 'VN', en: 'Vietnam', fr: 'Vietnam' },
        { code: 'YE', en: 'Yemen', fr: 'Yémen' },
        { code: 'ZM', en: 'Zambia', fr: 'Zambie' },
        { code: 'ZW', en: 'Zimbabwe', fr: 'Zimbabwe' },
      ];

      // Get current language (assuming you have this.lang from your mixins)
      const currentLang = this.lang || this.$i18n.locale || 'en';

      return countries.map(country => ({
        label: currentLang === 'fr' ? country.fr : country.en,
        value: country.code,
        disabled: country.code === ''
      }));
    },
  },
  watch: {
    // Clear region model if the country chosen does not have any region
    countryInputField (country) {
      if (!(country === 'CA' || country === 'US')) {
        this.inputFields.region = '';
      }
    },
  },
  methods: {
    closeSelect (refName) {
      const ref = this.$refs[refName]
      // Dynamic refs in v-for return arrays
      const select = Array.isArray(ref) ? ref[0] : ref
      if (select) {
        select.open = false
      }
    },
    // Format the password feedback message
    formatFeedback (condition) {
      let message;
      let short = condition[0] === 'password_has_accepted_char' ? '_short' : '';

      if (condition[0] === 'password_has_length') {
        short = '_shorter';
      }

      if (condition[1] === false) {
        message = this.$t(`validation.${condition[0]}`,
          { feedback: this.$t(`validation.negative_feedback${short}`) });
      } else {
        message = this.$t(`validation.${condition[0]}`,
          { feedback: this.$t(`validation.positive_feedback${short}`) });
      }

      return message;
    },
    // Return the right label message in the form loop
    formatInputLabel (field) {
      const required = field.validations.required || field.type === 'autocomplete';

      return `${this.$t(`form.${field.name}`)}${required ? '*' : ''}`;
    },
    // Check if the form has invalid Front-End fields
    fieldIsInvalid (field) {
      let condition = this.errors.items.find(item => item.field === field);

      if (field === 'passwordConfirmation') {
        condition = condition
          || this.bothPasswordMatch === false;
      } else if (field === 'email') {
        condition = condition
          || this.emailAlreadyUsed;
      }

      return condition;
    },
    // Call API to check if email exists
    async checkEmail () {
      this.emailAlreadyUsed = false;
      const email = this.inputFields.email;

      if (this.isCreate && email !== '' && email !== this.cachedEmail) {
        this.$axios.get('/api/verify/email', { params: { email } })
          // If success, validate email
          .then(() => {
            this.emailAlreadyUsed = false;
          })
          // If error 409, give the email field an error
          .catch((error) => {
            const code = get(error, 'response.data.status.code', null)
            if (code === 409) {
              this.emailAlreadyUsed = true;
            }
          })
          // Always cache the email to prevent too many API calls
          // (will call the API only if inputed email is different)
          .then(() => {
            this.cachedEmail = email;
          });
      }
    },
    // On input, check if certain conditions are met for the password to be valid
    handlePasswordInput (event) {
      if (['password'].includes(event.target.name)) {
        const { value } = event.target;
        this.checkPasswordMatch();

        if (this.passwordRequirements) {
          this.passwordFeedbackVisible = true;
          this.passwordRequirements.password_has_accepted_char = /^[^\s]+$/.test(value)
            && value.length >= 1;
          this.passwordRequirements.password_has_length = value.length >= 8 && value.length <= 20;
          this.passwordRequirements.password_has_capital = value.toLowerCase() !== value;
          this.passwordRequirements.password_has_lowercase = value.toUpperCase() !== value;
          this.passwordRequirements.password_has_number = /\d/.test(value);
          this.passwordFormatValid = Object.values(this.passwordRequirements).every(item => item);
        }
      }
    },
    // Return true or false if both password fields match or no
    checkPasswordMatch () {
      const { password, passwordConfirmation } = this.inputFields;
      this.bothPasswordMatch = password === passwordConfirmation;
    },
    // Show / hide the address on "Manual entry" link click
    handleToggleAddress () {
      this.manualEntry = !this.manualEntry;
    },
    // Return the right message according to the email validation (already used or general mandatory)
    formatValidationMessage (field) {
      if (field.name === 'email' && this.emailAlreadyUsed) {
        return this.$t('validation.email_already_used');
      }
      return this.$t(`validation.${field.validationMessage}`);
    },
    // When the location found
    // @param {Object} addressData Data of the found location
    // @param {Object} placeResultData PlaceResult object
    // @param {String} id Input container ID
    fillAddressData (addressData, placeResultData) {
      this.manualEntry = true;
      const { country } = addressData;
      const region = addressData.administrative_area_level_1;
      const ISOCountry = this.getISOcode(country, placeResultData);
      const ISORegion = this.getISOcode(region, placeResultData);
      this.inputFields.country = ISOCountry;
      this.inputFields.region = ISORegion;
      this.inputFields.city = addressData.locality;
      this.inputFields.postalCode = addressData.postal_code;
      this.inputFields.address = placeResultData.name;
    },
    /**
     * Get region options based on country
     * @param {String} country - Country code (CA or US)
     * @returns {Array} - Array of region options
     */
    getRegionOptions (country) {
      // Get current language
      const currentLang = this.lang || this.$i18n.locale || 'en';

      if (country === 'CA') {
        const provinces = [
          { code: 'AB', en: 'Alberta', fr: 'Alberta' },
          { code: 'BC', en: 'British Columbia', fr: 'Colombie-Britannique' },
          { code: 'MB', en: 'Manitoba', fr: 'Manitoba' },
          { code: 'NB', en: 'New Brunswick', fr: 'Nouveau-Brunswick' },
          { code: 'NL', en: 'Newfoundland and Labrador', fr: 'Terre-Neuve-et-Labrador' },
          { code: 'NT', en: 'Northwest Territories', fr: 'Territoires du Nord-Ouest' },
          { code: 'NS', en: 'Nova Scotia', fr: 'Nouvelle-Écosse' },
          { code: 'NU', en: 'Nunavut', fr: 'Nunavut' },
          { code: 'ON', en: 'Ontario', fr: 'Ontario' },
          { code: 'PE', en: 'Prince Edward Island', fr: 'Île-du-Prince-Édouard' },
          { code: 'QC', en: 'Quebec', fr: 'Québec' },
          { code: 'SK', en: 'Saskatchewan', fr: 'Saskatchewan' },
          { code: 'YT', en: 'Yukon', fr: 'Yukon' },
        ];

        return provinces.map(province => ({
          label: currentLang === 'fr' ? province.fr : province.en,
          value: province.code
        }));
      } else if (country === 'US') {
        const states = [
          { code: 'AL', en: 'Alabama', fr: 'Alabama' },
          { code: 'AK', en: 'Alaska', fr: 'Alaska' },
          { code: 'AZ', en: 'Arizona', fr: 'Arizona' },
          { code: 'AR', en: 'Arkansas', fr: 'Arkansas' },
          { code: 'CA', en: 'California', fr: 'Californie' },
          { code: 'CO', en: 'Colorado', fr: 'Colorado' },
          { code: 'CT', en: 'Connecticut', fr: 'Connecticut' },
          { code: 'DE', en: 'Delaware', fr: 'Delaware' },
          { code: 'FL', en: 'Florida', fr: 'Floride' },
          { code: 'GA', en: 'Georgia', fr: 'Géorgie' },
          { code: 'HI', en: 'Hawaii', fr: 'Hawaï' },
          { code: 'ID', en: 'Idaho', fr: 'Idaho' },
          { code: 'IL', en: 'Illinois', fr: 'Illinois' },
          { code: 'IN', en: 'Indiana', fr: 'Indiana' },
          { code: 'IA', en: 'Iowa', fr: 'Iowa' },
          { code: 'KS', en: 'Kansas', fr: 'Kansas' },
          { code: 'KY', en: 'Kentucky', fr: 'Kentucky' },
          { code: 'LA', en: 'Louisiana', fr: 'Louisiane' },
          { code: 'ME', en: 'Maine', fr: 'Maine' },
          { code: 'MD', en: 'Maryland', fr: 'Maryland' },
          { code: 'MA', en: 'Massachusetts', fr: 'Massachusetts' },
          { code: 'MI', en: 'Michigan', fr: 'Michigan' },
          { code: 'MN', en: 'Minnesota', fr: 'Minnesota' },
          { code: 'MS', en: 'Mississippi', fr: 'Mississippi' },
          { code: 'MO', en: 'Missouri', fr: 'Missouri' },
          { code: 'MT', en: 'Montana', fr: 'Montana' },
          { code: 'NE', en: 'Nebraska', fr: 'Nebraska' },
          { code: 'NV', en: 'Nevada', fr: 'Nevada' },
          { code: 'NH', en: 'New Hampshire', fr: 'New Hampshire' },
          { code: 'NJ', en: 'New Jersey', fr: 'New Jersey' },
          { code: 'NM', en: 'New Mexico', fr: 'Nouveau-Mexique' },
          { code: 'NY', en: 'New York', fr: 'New York' },
          { code: 'NC', en: 'North Carolina', fr: 'Caroline du Nord' },
          { code: 'ND', en: 'North Dakota', fr: 'Dakota du Nord' },
          { code: 'OH', en: 'Ohio', fr: 'Ohio' },
          { code: 'OK', en: 'Oklahoma', fr: 'Oklahoma' },
          { code: 'OR', en: 'Oregon', fr: 'Oregon' },
          { code: 'PA', en: 'Pennsylvania', fr: 'Pennsylvanie' },
          { code: 'RI', en: 'Rhode Island', fr: 'Rhode Island' },
          { code: 'SC', en: 'South Carolina', fr: 'Caroline du Sud' },
          { code: 'SD', en: 'South Dakota', fr: 'Dakota du Sud' },
          { code: 'TN', en: 'Tennessee', fr: 'Tennessee' },
          { code: 'TX', en: 'Texas', fr: 'Texas' },
          { code: 'UT', en: 'Utah', fr: 'Utah' },
          { code: 'VT', en: 'Vermont', fr: 'Vermont' },
          { code: 'VA', en: 'Virginia', fr: 'Virginie' },
          { code: 'WA', en: 'Washington', fr: 'Washington' },
          { code: 'WV', en: 'West Virginia', fr: 'Virginie-Occidentale' },
          { code: 'WI', en: 'Wisconsin', fr: 'Wisconsin' },
          { code: 'WY', en: 'Wyoming', fr: 'Wyoming' },
        ];

        return states.map(state => ({
          label: currentLang === 'fr' ? state.fr : state.en,
          value: state.code
        }));
      }
      return [];
    },
    // Return the right address ISO code if available to be use in the Google Autocomplete
    getISOcode (item, resultData) {
      const data = resultData.address_components.find((one) => {
        if (one.long_name === item) {
          return one.short_name;
        }
      });
      return data && data.short_name ? data.short_name : item;
    },
    // Handle form validation + API call + Parent redirects
    async handleSubmit (recaptchaToken) {
      this.isLoading = true;
      this.formMessage = null;
      let termsCondition = true;
      const veeValidation = await this.$validator.validate();

      await this.checkEmail();

      if (this.isCreate) {
        termsCondition = this.termsChecked;
      }

      this.checkPasswordMatch();

      // Custom validation on create and reset forms
      let customValidation = true;

      if (this.isCreate) {
        // Check password + email
        customValidation = this.bothPasswordMatch
          && this.passwordFormatValid
          && !this.emailAlreadyUsed;
      } else if (this.isReset) {
        // Check password
        customValidation = this.bothPasswordMatch
          && this.passwordFormatValid;
      }

      if (veeValidation
        && customValidation
        && recaptchaToken
        && termsCondition) {
        let endpoint;
        let post;

        if (this.isLogin) {
          endpoint = '/api/login',
            post = {
              email: this.inputFields.email,
              password: this.inputFields.password,
            };
        } else if (this.isCreate) {
          endpoint = '/api/account/create',
            post = {
              firstname: this.inputFields.firstName,
              lastname: this.inputFields.lastName,
              email: this.inputFields.email,
              password: this.inputFields.password,
              password_confirmation: this.inputFields.passwordConfirmation,
              phone: this.inputFields.phone,
              cellphone: this.inputFields.cellphone,
              gender: this.inputFields.gender || 'U',
              birthdate: this.inputFields.birthDate,
              address: this.inputFields.address,
              country: this.inputFields.country,
              state: this.inputFields.region,
              city: this.inputFields.city,
              postal_code: this.inputFields.postalCode,
              lang: this.lang,
              origin: this.site,
              optin: 'Y',
            };
          if (this.isSiteGamification) {
            Object.assign(post, { chgmOptin: this.inputFields.chgmOptin });
          }
        // Todo: Refactor (remove & move in Axios) this when API is available
        } else if (this.isForgot) {
          endpoint = '/api/password/forgot',
            post = {
              email: this.inputFields.email,
              locale: this.lang,
            };
        } else if (this.isReset) {
          const { token } = this.$route.query;
          endpoint = '/api/password/reset',
            post = {
              token,
              password: this.inputFields.password,
              password_confirmation: this.inputFields.passwordConfirmation,
            };
        }

        // API call
        await this.axios.post(endpoint, post)
          .then((response) => {
            if (this.isLogin || this.isCreate) {
              const { token } = response.data.data;
              const tokenQuery = token ? `?token=${token}` : '';
              let originAppQuery = '';
              let chgmOptinQuery = '';

              if ((this.isSiteClub1909 || this.isSiteHabsPlus) && this.$route.query.origin === 'app') {
                originAppQuery = token ? '&origin=app' : '?origin=app';
              }

              if (this.isCreate && post.chgmOptin) {
                chgmOptinQuery = `&chgm_optin=${post.chgmOptin}`;
              }

              const parsedUri = `${this.redirectUri}${tokenQuery}${originAppQuery}${chgmOptinQuery}`;

              if ((this.isLogin && this.inputFields.rememberMe) || this.isCreate) {
                Cookies.set('ch-sso-token', token, { expires: 30, domain: '.canadiens.com' })
              }

              // Send event to analytics
              this.$gtag.event(this.type, {
                'event_category': 'engagement',
                'event_label': post.email
              });

              this.redirect(parsedUri, this.type);
            } else if (this.isForgot || this.isReset) {
              this.$emit('submitted', get(post, 'email', null));
            }
          })
            // Handle API errors
          .catch((error) => {
            const { response } = error;
            let errorLabel = 'error.something_went_wrong';

            if (response !== undefined) {
              const { msg } = response.data.data;
              errorLabel = msg;
            }

            this.isLoading = false;
            this.formMessage = this.$t(errorLabel);
          });
      } else {
        // Handle Front-End validation errors
        this.isLoading = false;
        this.formMessage = this.$t('error.form');

        if (this.isCreate && !this.termsChecked) {
          this.formMessage = this.$t('error.terms');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// General
.form {
  text-align: left;
  margin: auto;
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: $font-size-base;

  @include rem(margin-bottom, 10px);

  &.is-disabled {
    pointer-events: none;
  }
}

.form-select,
/deep/ .date-dropdown__select {
  padding-right: $spacer * 2;
  background: transparent url('../assets/img/chevron.png') no-repeat;

  @include rem(background-size, 13px);

  background-position: right $spacer/2 center;
  font-weight: $font-weight-base;
  color: black;
  cursor: pointer;
}

.form-input,
/deep/ .date-dropdown__select {
  background-color: transparent;
  appearance: none;
  color: $white;
  padding: $padding-form;
  margin-bottom: $spacer;
  border: 2px solid $color-danger;
  border-radius: $border-radius-inputs;
  font-size: $font-size-base;
  line-height: normal;
  font-weight: $font-weight-base;
  width: 100%;

  @include rem(min-height, 45px);

  .form--light & {
    border: 1px solid $color-border-base;
  }

  &::placeholder {
    color: $white;
    opacity: 0.4;
  }

  &:focus,
  &:active,
  &:target {
    background-color: rgba(255, 255, 255, 0.2);
    box-shadow: none;
    outline: none;

    &::placeholder {
      opacity: 1;
    }
  }

  // State
  &.is-invalid {
    border: 2px solid $color-danger;
    background: rgba(196, 15, 57, 0.1);
    color: $white;

    &::placeholder {
      color: $color-danger;
    }

    //.form--light & {
    //  border-bottom: 1px solid $color-border-base;
    //}
  }

  &.is-success {
    border: 2px solid $color-success;
  }

  &.is-disabled {
    background-color: darken($color-gray-light, 5%);
    pointer-events: none;
  }
}

.form-date-disclaimer {
  margin: -$spacer 0 $spacer;
  font-size: $font-size-sm;
}

// Password feedback
.form-password-feedback {
  background: rgba(255, 255, 255, 0.8);
  padding: $spacer;
  backdrop-filter: blur(10px);

  @include rem(margin, 0 0 16px);

  border: 2px solid $color-danger;

  &::placeholder {
    color: $white;
  }

  div {
    display: flex;
    font-size: $font-size-sm;
  }

  i {
    margin: $spacer/10 $spacer/3 0 0;

    @include rem(font-size, 16px);
  }

  span,
  i {
    &.is-danger {
      color: $color-danger;
    }

    &.is-success {
      color: $color-success !important;
    }
  }

  &.is-success {
    border-color: $color-success !important;
  }

  .form:not(.form--light) & span {
    color: $white;
  }
}

// Manual entry
.form-manual-entry {
  color: inherit;
  position: relative;
  align-self: flex-end;
  margin: -$spacer 0 $spacer;
  font-size: $font-size-sm;
  z-index: 1;
}

// Submit
.form-submit {
  margin-top: $spacer;

  > .btn {
    border-radius: $border-radius-inputs;
    padding: $padding-form;
    width: 100%;

    @media (min-width: $bp-sm) {
      margin-right: $spacer*2;
    }
  }
}

// Terms
.form-terms {
  font-size: $font-size-sm;
  margin-bottom: $spacer;

  @media (min-width: $bp-sm) {
    margin-bottom: 0;
  }
}

// Link Alt
.link-alt {
  margin-top: $spacer;

  @media (min-width: $bp-sm) {
    margin-top: 0;
  }
}

// Separator
hr {
  margin: $spacer*2 0 $spacer*3;
  transition: $transition-base;
}

// Modifiers
.form-submit--multiple-links {
  justify-content: space-between;

  > a {
    margin-left: 0;
  }
}
</style>
